"use client";

import { ChevronDown, Settings, User, Shield, ArrowLeftRight } from "lucide-react";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from "@/components/ui/sidebar";
import { useAuth } from "@/context/auth.context";
import { useNavigate } from "react-router-dom";
import { CooperativeLogo } from "@/components/ui/cooperative-logo";

export function PortalSwitcher() {
  const { user, portalMode, switchPortal, canSwitchPortal } = useAuth();
  const navigate = useNavigate();

  const handleSwitch = (mode: "admin" | "member") => {
    switchPortal(mode);
    if (mode === "admin") {
      navigate("/dashboard");
    } else {
      navigate("/member/dashboard");
    }
  };

  const getRoleDisplayName = () => {
    const leadershipRole =
      user?.leadershipRole ||
      user?.role?.name ||
      (typeof user?.role === "string" ? user.role : null);

    if (
      leadershipRole &&
      ["Technical Support", "President", "Secretary", "Accountant"].includes(
        leadershipRole
      )
    ) {
      return leadershipRole;
    }

    if (user?.isAdmin) {
      return "Case d'Entraide";
    }

    return leadershipRole || "Member";
  };

  if (!canSwitchPortal) {
    return (
      <SidebarMenu>
        <SidebarMenuItem>
          <SidebarMenuButton
            size="lg"
            className="flex h-auto min-w-0 cursor-default items-center gap-3 rounded-xl px-3 py-3 transition-colors hover:bg-[#F8FAFC]"
          >
            <div className="flex shrink-0 items-center justify-center">
              <CooperativeLogo size={40} circular className="ring-2 ring-[#0A9F43]/10" />
            </div>
            <div className="grid min-w-0 flex-1 text-left text-sm leading-tight group-data-[collapsible=icon]:opacity-0 transition-opacity duration-100">
              <span
                className="truncate font-bold text-[#111827]"
                title="CAISSE D'ENTRAIDE IPRC"
              >
                CAISSE D'ENTRAIDE IPRC
              </span>
              <span className="truncate text-xs font-medium text-[#6B7280]">
                {portalMode === "admin" ? "Management" : "Member Portal"}
              </span>
            </div>
          </SidebarMenuButton>
        </SidebarMenuItem>
      </SidebarMenu>
    );
  }

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size="lg"
              className="flex h-auto min-w-0 cursor-pointer items-center gap-3 rounded-xl px-3 py-3 transition-colors hover:bg-[#F8FAFC] data-[state=open]:bg-[#F8FAFC]"
            >
              <div className="flex shrink-0 items-center justify-center">
                <CooperativeLogo size={40} circular className="ring-2 ring-[#0A9F43]/10" />
              </div>
              <div className="grid min-w-0 flex-1 text-left text-sm leading-tight group-data-[collapsible=icon]:opacity-0 transition-opacity duration-100">
                <span
                  className="truncate font-bold text-[#111827]"
                  title="CAISSE D'ENTRAIDE IPRC"
                >
                  CAISSE D'ENTRAIDE IPRC
                </span>
                <span className="truncate text-xs font-medium text-[#6B7280]">
                {portalMode === "admin" ? "Cooperative Account" : "Member Portal"}
                </span>
              </div>
              <ChevronDown className="size-4 shrink-0 text-[#9CA3AF] group-data-[collapsible=icon]:opacity-0 transition-opacity duration-100" />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-64 rounded-lg"
            side="bottom"
            align="start"
            sideOffset={4}
          >
            <DropdownMenuLabel className="p-0 font-normal">
              <div className="flex items-center gap-2 px-2 py-1.5">
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#0A9F43]/10">
                  <Shield className="h-4 w-4 text-[#0A9F43]" />
                </div>
                <div className="grid flex-1 text-left leading-tight">
                  <span className="truncate font-semibold text-sm">
                    {user?.fullName || user?.name || "Leader"}
                  </span>
                  <span className="truncate text-xs text-[#050500]/70">
                    {getRoleDisplayName()}
                  </span>
                </div>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={() => handleSwitch("admin")}
              className={`cursor-pointer gap-2 ${portalMode === "admin" ? "bg-[#0A9F43]/5" : ""}`}
            >
              <Settings className="h-4 w-4" />
              <div className="flex flex-col">
                <span className="text-sm font-medium">Cooperative Account</span>
                <span className="text-xs text-muted-foreground">
                  Gérer les opérations de la caisse
                </span>
              </div>
              {portalMode === "admin" && (
                <span className="ml-auto text-xs text-[#0A9F43] font-medium">Active</span>
              )}
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => handleSwitch("member")}
              className={`cursor-pointer gap-2 ${portalMode === "member" ? "bg-[#0A9F43]/5" : ""}`}
            >
              <User className="h-4 w-4" />
              <div className="flex flex-col">
                <span className="text-sm font-medium">My Account</span>
                <span className="text-xs text-muted-foreground">
                  View your personal account
                </span>
              </div>
              {portalMode === "member" && (
                <span className="ml-auto text-xs text-[#0A9F43] font-medium">Active</span>
              )}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <div className="px-2 py-1.5">
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <ArrowLeftRight className="h-3 w-3" />
                <span>Switch between management and personal views</span>
              </div>
            </div>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}
